/**
 * 标准配合距离与重合偏移求解器。
 *
 * 文件职责：
 * 1. 封装 Coincident / Distance 共用的偏移求解；
 * 2. 维护 SOLIDWORKS 距离分支、反转尺寸和中心/最小/最大距离语义；
 * 3. 只消费 plain data 与几何描述符，不依赖 Viewer 或业务控制器。
 */

import * as THREE from 'three';
import type { ComponentTransformSnapshot } from '../component-instance';
import {
  AssemblyMateType,
  MateAlignment,
  MateEntityKind,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import type {
  AssemblyMateSolverWorldBounds,
  StandardMateApplyResult,
  StandardMateMovingSideResolution,
} from './StandardMateSolverTypes';
import { StandardMateDistanceBranchPolicy } from './StandardMateSolverTypes';
import { resolveStandardMateSignedDistance } from './dimension/StandardMateDimension';
import { resolveStandardMateDistanceOffsetForSolver } from './dimension/StandardMateDistanceTarget';
import { solveConcentricMate } from './StandardMateConcentricLockSolver';
import { solveSurfaceCoincidentMate } from './StandardMateSurfaceCoincident';
import {
  applyWorldDeltaToTransform,
  createRotationDeltaBetweenDirections,
  createTranslationDelta,
  normalizeVector,
  resolveAxisGeometry,
  resolvePerpendicularDirection,
  resolvePlaneGeometry,
  resolvePointGeometry,
  resolveRepresentativePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
  type PointGeometry,
} from './geometry/StandardMateGeometryAccess';

/** 距离求解容差。 */
const DISTANCE_SOLVER_EPSILON = 1e-7;

/**
 * 将 worldBounds 元组转换成 Vector3。
 *
 * @param value 三元组。
 * @returns Vector3。
 */
function vectorFromBoundsTuple(value: readonly [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2]);
}

/**
 * 计算世界包围盒中心。
 *
 * @param bounds 世界包围盒。
 * @returns 包围盒中心。
 */
function resolveWorldBoundsCenter(bounds: AssemblyMateSolverWorldBounds): THREE.Vector3 {
  return vectorFromBoundsTuple(bounds.min).add(vectorFromBoundsTuple(bounds.max)).multiplyScalar(0.5);
}

/**
 * 计算世界包围盒尺寸。
 *
 * @param bounds 世界包围盒。
 * @returns 包围盒尺寸。
 */
function resolveWorldBoundsSize(bounds: AssemblyMateSolverWorldBounds): THREE.Vector3 {
  return vectorFromBoundsTuple(bounds.max).sub(vectorFromBoundsTuple(bounds.min));
}

/**
 * 计算包围盒在指定方向上的半投影长度。
 *
 * @param bounds 世界包围盒。
 * @param direction 单位方向。
 * @returns 半投影长度。
 */
function calculateWorldBoundsHalfExtentAlongDirection(
  bounds: AssemblyMateSolverWorldBounds,
  direction: THREE.Vector3,
): number {
  const size = resolveWorldBoundsSize(bounds);
  return (
    Math.abs(direction.x) * size.x
    + Math.abs(direction.y) * size.y
    + Math.abs(direction.z) * size.z
  ) * 0.5;
}

/**
 * 对世界包围盒应用世界矩阵并返回新的轴对齐包围盒。
 *
 * @param bounds 原始世界包围盒。
 * @param worldDelta 世界空间变换矩阵。
 * @returns 变换后的世界包围盒。
 */
function transformWorldBounds(
  bounds: AssemblyMateSolverWorldBounds,
  worldDelta: THREE.Matrix4,
): AssemblyMateSolverWorldBounds {
  const min = vectorFromBoundsTuple(bounds.min);
  const max = vectorFromBoundsTuple(bounds.max);
  const transformedBounds = new THREE.Box3();
  [min.x, max.x].forEach((x) => {
    [min.y, max.y].forEach((y) => {
      [min.z, max.z].forEach((z) => {
        transformedBounds.expandByPoint(new THREE.Vector3(x, y, z).applyMatrix4(worldDelta));
      });
    });
  });
  return {
    min: [transformedBounds.min.x, transformedBounds.min.y, transformedBounds.min.z],
    max: [transformedBounds.max.x, transformedBounds.max.y, transformedBounds.max.z],
  };
}

/**
 * 解析临时平面重合预览中由 bbox 约束的面内位移。
 *
 * @param side 移动侧解析结果。
 * @param targetPlane 目标平面。
 * @param worldDelta 已完成法向贴合后的世界变换。
 * @param representativeInPlaneTranslation 代表点贴合给出的面内位移。
 * @param epsilon 容差。
 * @returns 代表点异常时返回 bbox 邻近放置位移；否则返回 `null`。
 */
function resolveBoundedPlaneCoincidentInitialPlacementTranslation(
  side: StandardMateMovingSideResolution,
  targetPlane: NonNullable<ReturnType<typeof resolvePlaneGeometry>>,
  worldDelta: THREE.Matrix4,
  representativeInPlaneTranslation: THREE.Vector3,
  epsilon: number,
): THREE.Vector3 | null {
  const fixedBounds = side.fixedComponent.worldBounds;
  const movingBounds = side.movingComponent.worldBounds;
  if (!fixedBounds || !movingBounds) {
    return null;
  }

  const fixedSize = resolveWorldBoundsSize(fixedBounds);
  const movingSize = resolveWorldBoundsSize(movingBounds);
  const boundsScale = Math.max(fixedSize.length(), movingSize.length(), epsilon);
  if (representativeInPlaneTranslation.length() <= boundsScale * 4) {
    return null;
  }

  const solvedMovingBounds = transformWorldBounds(movingBounds, worldDelta);
  const fixedCenter = resolveWorldBoundsCenter(fixedBounds);
  const movingCenter = resolveWorldBoundsCenter(solvedMovingBounds);
  const inPlaneDirection = movingCenter.clone().sub(fixedCenter);
  inPlaneDirection.addScaledVector(
    targetPlane.normal,
    -inPlaneDirection.dot(targetPlane.normal),
  );
  const direction = normalizeVector(inPlaneDirection, epsilon)
    ?? resolvePerpendicularDirection(targetPlane.normal, epsilon);
  const placementGap = Math.max(epsilon * 10, DISTANCE_SOLVER_EPSILON);
  const targetCenter = fixedCenter.clone().addScaledVector(
    direction,
    calculateWorldBoundsHalfExtentAlongDirection(fixedBounds, direction)
      + calculateWorldBoundsHalfExtentAlongDirection(solvedMovingBounds, direction)
      + placementGap,
  );
  const boundedTranslation = targetCenter.sub(movingCenter);
  boundedTranslation.addScaledVector(
    targetPlane.normal,
    -boundedTranslation.dot(targetPlane.normal),
  );
  return boundedTranslation;
}

/**
 * 将点投影到轴线上。
 *
 * @param point 待投影点。
 * @param axis 目标轴线。
 * @returns 投影点。
 */
function projectPointToAxis(point: THREE.Vector3, axis: AxisGeometry): THREE.Vector3 {
  const originToPoint = point.clone().sub(axis.origin);
  return axis.origin.clone().addScaledVector(axis.direction, originToPoint.dot(axis.direction));
}

/**
 * 解析 Distance / Coincident 可按点处理的几何。
 *
 * 设计说明：
 * SOLIDWORKS 的 Distance Mate 允许 Sphere 与 Plane / Line / Point / Sphere 组合。
 * 该类距离不使用圆柱专属 Center/Min/Max 条件，求解时按球心作为点参与定位。
 *
 * @param entity Mate 实体。
 * @returns 点几何；无法按点求解时返回 null。
 */
function resolveDistancePointGeometry(entity: MateEntityRef): PointGeometry | null {
  const point = resolvePointGeometry(entity);
  if (point) {
    return point;
  }
  const sphere = resolveSphereGeometry(entity);
  return sphere ? { point: sphere.center } : null;
}

/**
 * 判断轴线是否带有圆形距离语义。
 *
 * @param axis 轴线几何。
 * @returns 是否存在有效半径。
 */
function hasRoundAxisDistanceGeometry(axis: AxisGeometry): boolean {
  return typeof axis.radius === 'number'
    && Number.isFinite(axis.radius)
    && axis.radius > DISTANCE_SOLVER_EPSILON;
}

/**
 * 解析圆形轴线用于距离配合的中心线参考点。
 *
 * 设计说明：圆柱面选择的 axisOrigin 可能位于无限轴线上任意位置；若直接使用该点，
 * 拖动后平面-圆柱距离会退化到轴线与平面的交点距离。SOLIDWORKS 的 Center/Min/Max
 * 距离条件以被选曲面附近的中心线截面为基准，因此这里把曲面代表点投影回轴线。
 *
 * @param entity 轴线所属实体。
 * @param axis 轴线几何。
 * @returns 用于距离残差计算的轴线上参考点。
 */
function resolveAxisDistanceReferencePoint(
  entity: MateEntityRef,
  axis: AxisGeometry,
): THREE.Vector3 {
  const representativePoint = resolveRepresentativePointGeometry(entity)?.point ?? null;
  return representativePoint ? projectPointToAxis(representativePoint, axis) : axis.origin.clone();
}

/**
 * 解析当前移动侧使用的有符号尺寸距离。
 *
 * 设计说明：
 * 1. Distance Mate 的正方向由第一选择到第二选择定义；
 * 2. 当真实 fixed 或交互驱动策略要求“移动第一选择”时，求解公式中的 moving/fixed 顺序被反转；
 * 3. 此时必须同步反转有符号距离，否则会把第一选择移动到固定实体的另一侧；
 * 4. 平面-轴线距离存在独立的选择侧分支逻辑，会在进入该兜底函数前提前收口。
 *
 * @param mate 配合定义。
 * @param side 当前移动侧。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @returns 当前移动侧应使用的有符号距离。
 */
function resolveSignedDistanceForMovingSide(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  linearDimensionScale: number,
): number {
  const rawDistance = resolveStandardMateSignedDistance(mate, linearDimensionScale);

  return side.movingFirst ? -rawDistance : rawDistance;
}

/**
 * 解析有符号距离的符号。
 *
 * @param value 有符号距离。
 * @param epsilon 容差。
 * @returns 距离符号；距离退化时返回 null。
 */
function resolveSignedDistanceSign(value: number, epsilon: number): number | null {
  if (!Number.isFinite(value) || Math.abs(value) <= epsilon) {
    return null;
  }
  return value < 0 ? -1 : 1;
}

/**
 * 按 Mate 原始选择顺序解析平面-轴线距离所在侧。
 *
 * 设计说明：
 * 1. SOLIDWORKS 的平面-圆柱/轴 Distance 通过“反转尺寸”切换实体到另一侧；
 * 2. 拖动已提交 Mate 时不能因为另一侧位移更短就切换分支，否则跟随件会闪现到驱动件另一侧；
 * 3. 因此平面-轴线类距离使用创建 Mate 时的选择侧作为稳定分支，后续只由 flipped 反向。
 *
 * @param mate 用于读取选择瞬间几何的 Mate Definition。
 * @param epsilon 容差。
 * @returns 选择侧符号；非平面-轴线组合或距离退化时返回 null。
 */
function resolvePlaneAxisSelectionDistanceSign(
  mate: AssemblyMateDefinition,
  epsilon: number,
): number | null {
  const firstPlane = resolvePlaneGeometry(mate.firstEntity);
  const secondPlane = resolvePlaneGeometry(mate.secondEntity);
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);

  if (firstPlane && secondAxis) {
    const axisReferencePoint = hasRoundAxisDistanceGeometry(secondAxis)
      ? resolveAxisDistanceReferencePoint(mate.secondEntity, secondAxis)
      : secondAxis.origin;
    return resolveSignedDistanceSign(
      axisReferencePoint.sub(firstPlane.point).dot(firstPlane.normal),
      epsilon,
    );
  }

  if (firstAxis && secondPlane) {
    const axisReferencePoint = hasRoundAxisDistanceGeometry(firstAxis)
      ? resolveAxisDistanceReferencePoint(mate.firstEntity, firstAxis)
      : firstAxis.origin;
    return resolveSignedDistanceSign(
      axisReferencePoint.sub(secondPlane.point).dot(secondPlane.normal),
      epsilon,
    );
  }

  return null;
}

/**
 * 解析平面-轴线 Distance 的稳定有符号尺寸。
 *
 * @param currentMate 当前求解步 Mate Definition。
 * @param sourceMate 创建 Mate 时的原始 Definition。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @param epsilon 容差。
 * @returns 稳定有符号距离；不适用时返回 null。
 */
function resolvePlaneAxisSignedDistanceFromSelectionBranch(
  currentMate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  linearDimensionScale: number,
  epsilon: number,
): number | null {
  if (currentMate.mateType !== AssemblyMateType.Distance) {
    return null;
  }
  const selectionSign = resolvePlaneAxisSelectionDistanceSign(sourceMate, epsilon);
  if (selectionSign === null) {
    return null;
  }
  const dimensionSign = currentMate.dimension?.flipped ? -selectionSign : selectionSign;
  const dimensionValue = Math.abs(resolveStandardMateSignedDistance(currentMate, linearDimensionScale));
  return dimensionSign * dimensionValue;
}

/**
 * 在已提交配合限制下求解平面-平面偏移。
 *
 * 设计说明：
 * 1. 两条同轴心已经把组件径向位移和绕轴旋转锁住时，后续 Coincident 只能消耗公共轴上的滑移自由度；
 * 2. 若继续沿平面法向做通用平移，会把孔轴推离已提交 Concentric，最终被 residual 判成破坏既有配合；
 * 3. 这里只处理“不需要再旋转、仅靠公共轴平移即可达到目标距离”的分支，无法满足时明确返回未解。
 *
 * @param input 受限平移求解输入。
 * @returns 受限求解结果；未传入有效轴时返回 null。
 */
function solvePlanePlaneOffsetWithConstrainedTranslation(input: {
  /** 当前移动侧。 */
  readonly side: StandardMateMovingSideResolution;
  /** 固定侧平面。 */
  readonly targetPlane: NonNullable<ReturnType<typeof resolvePlaneGeometry>>;
  /** 移动侧平面。 */
  readonly movingPlane: NonNullable<ReturnType<typeof resolvePlaneGeometry>>;
  /** 目标有符号距离。 */
  readonly distance: number;
  /** 已提交配合允许的平移轴。 */
  readonly constrainedTranslationAxis: THREE.Vector3 | null;
  /** 数值容差。 */
  readonly epsilon: number;
}): StandardMateApplyResult | null {
  const constrainedDirection = input.constrainedTranslationAxis
    ? normalizeVector(input.constrainedTranslationAxis, input.epsilon)
    : null;
  if (!constrainedDirection) {
    return null;
  }

  /**
   * 两条已提交同轴心会移除绕轴旋转自由度；此时 Coincident 只能接受当前姿态下已经共线的平面法向。
   * SOLIDWORKS 的同向/反向对齐是法向分支语义，若当前模型天然处于反向平行分支，仍然可以只靠轴向滑移完成重合。
   */
  const directionTolerance = Math.max(input.epsilon * 10, 1e-5);
  const sameNormalAngle = input.movingPlane.normal.angleTo(input.targetPlane.normal);
  const oppositeNormalAngle = Math.abs(Math.PI - sameNormalAngle);
  if (sameNormalAngle > directionTolerance && oppositeNormalAngle > directionTolerance) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '当前平面重合需要旋转组件，但已提交同轴心配合只允许沿公共轴滑移。',
    };
  }

  const currentSignedDistance = input.movingPlane.point
    .clone()
    .sub(input.targetPlane.point)
    .dot(input.targetPlane.normal);
  const remainingDistance = input.distance - currentSignedDistance;
  if (Math.abs(remainingDistance) <= input.epsilon) {
    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: input.side.movingComponent.componentInstanceId,
      transform: input.side.movingComponent.transform,
    };
  }

  const constrainedDistanceScale = constrainedDirection.dot(input.targetPlane.normal);
  if (Math.abs(constrainedDistanceScale) <= input.epsilon) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '当前平面重合所需位移不在已提交同轴心配合保留的公共轴滑移自由度内。',
    };
  }

  const translation = constrainedDirection.multiplyScalar(remainingDistance / constrainedDistanceScale);
  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: input.side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      input.side.movingComponent.transform,
      createTranslationDelta(translation),
      input.side.movingComponent.worldTransform,
    ),
  };
}

/**
 * 求解平面到平面的重合或距离。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param distance 目标距离。
 * @param epsilon 容差。
 * @param alignDirections 是否对齐平面法向。
 * @param alignRepresentativePoints 是否额外贴合代表点。
 * @param constrainedTranslationAxis 已提交配合保留的平移轴；为空时使用普通平面偏移求解。
 * @returns 应用结果。
 */
function solvePlanePlaneOffset(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  distance: number,
  epsilon: number,
  alignDirections: boolean,
  alignRepresentativePoints: boolean,
  constrainedTranslationAxis: THREE.Vector3 | null,
): StandardMateApplyResult {
  const targetPlane = resolvePlaneGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  if (!targetPlane || !movingPlane) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '当前配合需要两个平面实体。',
    };
  }

  const targetDirection = mate.alignment === MateAlignment.AntiAligned
    ? targetPlane.normal.clone().multiplyScalar(-1)
    : targetPlane.normal.clone();
  const constrainedResult = solvePlanePlaneOffsetWithConstrainedTranslation({
    side,
    targetPlane,
    movingPlane,
    distance,
    constrainedTranslationAxis,
    epsilon,
  });
  if (constrainedResult) {
    return constrainedResult;
  }

  const rotationDelta = alignDirections
    ? createRotationDeltaBetweenDirections(
        movingPlane.point,
        movingPlane.normal,
        targetDirection,
        epsilon,
      )
    : null;
  if (alignDirections && !rotationDelta) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '平面法向无法对齐。',
    };
  }

  const rotatedPoint = rotationDelta
    ? movingPlane.point.clone().applyMatrix4(rotationDelta)
    : movingPlane.point.clone();
  const currentSignedDistance = rotatedPoint.sub(targetPlane.point).dot(targetPlane.normal);
  const deltaDistance = distance - currentSignedDistance;
  const normalTranslation = targetPlane.normal.clone().multiplyScalar(deltaDistance);
  const normalTranslationDelta = createTranslationDelta(normalTranslation);
  let worldDelta = rotationDelta
    ? normalTranslationDelta.clone().multiply(rotationDelta)
    : normalTranslationDelta;

  if (alignRepresentativePoints) {
    const targetRepresentativePoint = resolveRepresentativePointGeometry(side.fixedEntity)?.point
      ?? targetPlane.point;
    const movingRepresentativePoint = resolveRepresentativePointGeometry(side.movingEntity)?.point
      ?? movingPlane.point;
    const solvedMovingRepresentativePoint = (rotationDelta
      ? movingRepresentativePoint.clone().applyMatrix4(rotationDelta)
      : movingRepresentativePoint.clone())
      .add(normalTranslation);
    const inPlaneTranslation = targetRepresentativePoint
      .clone()
      .sub(solvedMovingRepresentativePoint);
    inPlaneTranslation.addScaledVector(
      targetPlane.normal,
      -inPlaneTranslation.dot(targetPlane.normal),
    );
    const boundedInPlaneTranslation = resolveBoundedPlaneCoincidentInitialPlacementTranslation(
      side,
      targetPlane,
      worldDelta,
      inPlaneTranslation,
      epsilon,
    );
    const initialPlacementTranslation = boundedInPlaneTranslation ?? inPlaneTranslation;

    /**
     * Coincident 的持久语义只约束无限平面；这里仅在新建临时 Mate 预览阶段
     * 选择一个稳定初始解，让选中面先贴合，避免用户看到两个面共面但相隔很远。
     */
    if (initialPlacementTranslation.lengthSq() > epsilon * epsilon) {
      worldDelta = createTranslationDelta(initialPlacementTranslation).multiply(worldDelta);
    }
  }
  const translated = applyWorldDeltaToTransform(
    side.movingComponent.transform,
    worldDelta,
    side.movingComponent.worldTransform,
  );

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: translated,
  };
}

/**
 * 判断实体是否为圆边。
 *
 * @param entity Mate 实体。
 * @returns 是否为圆边实体。
 */
function isCircularEdgeEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.CircularEdge;
}

/**
 * 求解平面与圆边的重合。
 *
 * 设计说明：
 * SOLIDWORKS 的圆边-平面 Coincident 表示“圆边所在平面”与目标平面重合。
 * 圆边描述符在求解层表现为一条轴线，但不能套用通用“平面-轴线重合”，否则会把圆心轴线放到平面内，
 * 导致圆边所在平面反而与目标平面垂直。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param epsilon 容差。
 * @returns 求解结果；不是圆边-平面组合时返回 null。
 */
function solvePlaneCircularEdgeCoincident(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  epsilon: number,
): StandardMateApplyResult | null {
  const fixedPlane = resolvePlaneGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);

  if (fixedPlane && movingAxis && isCircularEdgeEntity(side.movingEntity)) {
    const targetNormal = mate.alignment === MateAlignment.AntiAligned
      ? fixedPlane.normal.clone().multiplyScalar(-1)
      : fixedPlane.normal;
    const rotationDelta = createRotationDeltaBetweenDirections(
      movingAxis.origin,
      movingAxis.direction,
      targetNormal,
      epsilon,
    );
    if (!rotationDelta) {
      return {
        status: MateSolveStatus.Unsolved,
        message: '圆边法向无法对齐到平面法向。',
      };
    }
    const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
    const rotatedCenter = movingAxis.origin.clone().applyMatrix4(rotationDelta);
    const signedDistance = rotatedCenter.sub(fixedPlane.point).dot(fixedPlane.normal);
    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: side.movingComponent.componentInstanceId,
      transform: applyWorldDeltaToTransform(
        rotatedTransform,
        createTranslationDelta(fixedPlane.normal.clone().multiplyScalar(-signedDistance)),
      ),
    };
  }

  if (fixedAxis && isCircularEdgeEntity(side.fixedEntity) && movingPlane) {
    const targetNormal = mate.alignment === MateAlignment.AntiAligned
      ? fixedAxis.direction.clone().multiplyScalar(-1)
      : fixedAxis.direction;
    const rotationDelta = createRotationDeltaBetweenDirections(
      movingPlane.point,
      movingPlane.normal,
      targetNormal,
      epsilon,
    );
    if (!rotationDelta) {
      return {
        status: MateSolveStatus.Unsolved,
        message: '平面法向无法对齐到圆边法向。',
      };
    }
    const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
    const rotatedPoint = movingPlane.point.clone().applyMatrix4(rotationDelta);
    const rotatedNormal = movingPlane.normal
      .clone()
      .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
      .normalize();
    const signedDistance = fixedAxis.origin.clone().sub(rotatedPoint).dot(rotatedNormal);
    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: side.movingComponent.componentInstanceId,
      transform: applyWorldDeltaToTransform(
        rotatedTransform,
        createTranslationDelta(rotatedNormal.multiplyScalar(signedDistance)),
      ),
    };
  }

  return null;
}

/**
 * 求解点到目标几何的重合或距离。
 *
 * @param side 移动侧。
 * @param distance 目标距离。
 * @param epsilon 容差。
 * @returns 应用结果。
 */
function solvePointOffset(
  side: StandardMateMovingSideResolution,
  distance: number,
  epsilon: number,
): StandardMateApplyResult {
  const movingPoint = resolveDistancePointGeometry(side.movingEntity);
  const fixedPoint = resolveDistancePointGeometry(side.fixedEntity);
  const fixedPlane = resolvePlaneGeometry(side.fixedEntity);
  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);

  let delta: THREE.Vector3 | null = null;
  if (movingPoint && fixedPoint) {
    const direction = movingPoint.point.clone().sub(fixedPoint.point);
    const fallback = new THREE.Vector3(1, 0, 0);
    const normalized = normalizeVector(direction, epsilon) ?? fallback;
    delta = fixedPoint.point.clone().addScaledVector(normalized, distance).sub(movingPoint.point);
  } else if (movingPoint && fixedPlane) {
    const signedDistance = movingPoint.point.clone().sub(fixedPlane.point).dot(fixedPlane.normal);
    delta = fixedPlane.normal.clone().multiplyScalar(distance - signedDistance);
  } else if (movingPoint && fixedAxis) {
    const originToPoint = movingPoint.point.clone().sub(fixedAxis.origin);
    const projection = fixedAxis.origin.clone().addScaledVector(fixedAxis.direction, originToPoint.dot(fixedAxis.direction));
    const radial = movingPoint.point.clone().sub(projection);
    const radialDirection = normalizeVector(radial, epsilon) ?? new THREE.Vector3(1, 0, 0);
    delta = projection.clone().addScaledVector(radialDirection, distance).sub(movingPoint.point);
  } else if (fixedPoint && movingPlane) {
    const signedDistance = fixedPoint.point.clone().sub(movingPlane.point).dot(movingPlane.normal);
    delta = movingPlane.normal.clone().multiplyScalar(signedDistance - distance);
  } else if (fixedPoint && movingAxis) {
    const projection = projectPointToAxis(fixedPoint.point, movingAxis);
    const radial = projection.clone().sub(fixedPoint.point);
    const radialDirection = normalizeVector(radial, epsilon)
      ?? resolvePerpendicularDirection(movingAxis.direction, epsilon);
    const targetProjection = fixedPoint.point.clone().addScaledVector(radialDirection, Math.abs(distance));
    delta = targetProjection.sub(projection);
  }

  if (!delta) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '点/球心距离配合目标几何暂不支持。',
    };
  }

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(
      side.movingComponent.transform,
      createTranslationDelta(delta),
    ),
  };
}

/**
 * 求解轴线到轴线的重合或距离。
 *
 * @param mate 配合定义。
 * @param side 移动侧。
 * @param distance 目标距离。
 * @param epsilon 容差。
 * @param alignDirections 是否对齐轴线方向。
 * @returns 应用结果。
 */
function solveAxisAxisOffset(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  distance: number,
  epsilon: number,
  alignDirections: boolean,
): StandardMateApplyResult {
  const targetAxis = resolveAxisGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  if (!targetAxis || !movingAxis) {
    return {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '当前配合需要两个可解析轴线实体。',
    };
  }

  const rotationDelta = alignDirections
    ? createRotationDeltaBetweenDirections(
        movingAxis.origin,
        movingAxis.direction,
        mate.alignment === MateAlignment.AntiAligned
          ? targetAxis.direction.clone().multiplyScalar(-1)
          : targetAxis.direction,
        epsilon,
      )
    : null;
  if (alignDirections && !rotationDelta) {
    return {
      status: MateSolveStatus.Unsolved,
      message: '轴线方向无法对齐。',
    };
  }

  const rotatedTransform = rotationDelta
    ? applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta)
    : side.movingComponent.transform;
  const rotatedOrigin = rotationDelta
    ? movingAxis.origin.clone().applyMatrix4(rotationDelta)
    : movingAxis.origin.clone();
  const projection = projectPointToAxis(rotatedOrigin, targetAxis);
  const radial = rotatedOrigin.clone().sub(projection);
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolvePerpendicularDirection(targetAxis.direction, epsilon);
  const targetOrigin = projection.addScaledVector(radialDirection, Math.abs(distance));
  const translation = targetOrigin.sub(rotatedOrigin);

  return {
    status: MateSolveStatus.Solved,
    movedComponentInstanceId: side.movingComponent.componentInstanceId,
    transform: applyWorldDeltaToTransform(rotatedTransform, createTranslationDelta(translation)),
  };
}

/**
 * 求解平面与轴线的重合或距离。
 *
 * @param side 移动侧。
 * @param distance 目标距离。
 * @param epsilon 容差。
 * @param alignGeometry 是否需要先对齐几何方向。
 * @param useAxisDistanceReference 是否使用圆形轴线中心参考点计算距离。
 * @returns 应用结果。
 */
function solvePlaneAxisOffset(
  side: StandardMateMovingSideResolution,
  distance: number,
  epsilon: number,
  alignGeometry: boolean,
  useAxisDistanceReference: boolean,
): StandardMateApplyResult {
  const fixedPlane = resolvePlaneGeometry(side.fixedEntity);
  const movingAxis = resolveAxisGeometry(side.movingEntity);
  if (fixedPlane && movingAxis) {
    const directionDot = movingAxis.direction.dot(fixedPlane.normal);
    let workingTransform = side.movingComponent.transform;
    let workingOrigin = movingAxis.origin.clone();
    let workingReferencePoint = useAxisDistanceReference && hasRoundAxisDistanceGeometry(movingAxis)
      ? resolveAxisDistanceReferencePoint(side.movingEntity, movingAxis)
      : resolveRepresentativePointGeometry(side.movingEntity)?.point ?? workingOrigin.clone();

    /**
     * 线-面距离只有在轴线平行于平面时才稳定。
     * 若用户先选择了一条斜线，预览阶段先旋转到平面内，再按法向距离定位。
     */
    if (alignGeometry && Math.abs(directionDot) > epsilon) {
      const projectedDirection = movingAxis.direction
        .clone()
        .sub(fixedPlane.normal.clone().multiplyScalar(directionDot));
      const normalizedProjectedDirection = normalizeVector(projectedDirection, epsilon);
      if (!normalizedProjectedDirection) {
        return {
          status: MateSolveStatus.Unsolved,
          message: '线方向无法稳定投影到平面内。',
        };
      }
      const rotationDelta = createRotationDeltaBetweenDirections(
        movingAxis.origin,
        movingAxis.direction,
        normalizedProjectedDirection,
        epsilon,
      );
      if (!rotationDelta) {
        return {
          status: MateSolveStatus.Unsolved,
          message: '线方向无法旋转到平面内。',
        };
      }
      workingTransform = applyWorldDeltaToTransform(workingTransform, rotationDelta);
      workingOrigin = workingOrigin.applyMatrix4(rotationDelta);
      workingReferencePoint = workingReferencePoint.applyMatrix4(rotationDelta);
    }

    const nextSignedDistance = (alignGeometry ? workingOrigin : workingReferencePoint)
      .sub(fixedPlane.point)
      .dot(fixedPlane.normal);
    const translation = fixedPlane.normal.clone().multiplyScalar(distance - nextSignedDistance);
    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: side.movingComponent.componentInstanceId,
      transform: applyWorldDeltaToTransform(workingTransform, createTranslationDelta(translation)),
    };
  }

  const fixedAxis = resolveAxisGeometry(side.fixedEntity);
  const movingPlane = resolvePlaneGeometry(side.movingEntity);
  if (fixedAxis && movingPlane) {
    if (!alignGeometry) {
      const fixedAxisReferencePoint = useAxisDistanceReference && hasRoundAxisDistanceGeometry(fixedAxis)
        ? resolveAxisDistanceReferencePoint(side.fixedEntity, fixedAxis)
        : fixedAxis.origin.clone();
      const signedDistance = fixedAxisReferencePoint.sub(movingPlane.point).dot(movingPlane.normal);
      const translation = movingPlane.normal.clone().multiplyScalar(signedDistance - distance);
      return {
        status: MateSolveStatus.Solved,
        movedComponentInstanceId: side.movingComponent.componentInstanceId,
        transform: applyWorldDeltaToTransform(
          side.movingComponent.transform,
          createTranslationDelta(translation),
        ),
      };
    }

    const targetNormal = resolvePerpendicularDirection(fixedAxis.direction, epsilon);
    const rotationDelta = createRotationDeltaBetweenDirections(
      movingPlane.point,
      movingPlane.normal,
      targetNormal,
      epsilon,
    );
    if (!rotationDelta) {
      return {
        status: MateSolveStatus.Unsolved,
        message: '平面无法旋转到包含轴线的方向。',
      };
    }
    const rotatedTransform = applyWorldDeltaToTransform(side.movingComponent.transform, rotationDelta);
    const rotatedPoint = movingPlane.point.clone().applyMatrix4(rotationDelta);
    const rotatedNormal = movingPlane.normal
      .clone()
      .applyMatrix3(new THREE.Matrix3().getNormalMatrix(rotationDelta))
      .normalize();
    const signedDistance = fixedAxis.origin.clone().sub(rotatedPoint).dot(rotatedNormal);
    const translation = rotatedNormal.multiplyScalar(signedDistance - distance);
    return {
      status: MateSolveStatus.Solved,
      movedComponentInstanceId: side.movingComponent.componentInstanceId,
      transform: applyWorldDeltaToTransform(rotatedTransform, createTranslationDelta(translation)),
    };
  }

  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message: '当前配合需要平面与线/轴实体。',
  };
}

/**
 * 按指定偏移量求解重合/距离配合。
 *
 * @param mate 配合定义。
 * @param side 当前移动侧。
 * @param distance 求解器内部使用的有符号偏移。
 * @param epsilon 数值容差。
 * @param alignRepresentativePoints 是否在临时预览中额外贴合代表点。
 * @param constrainedTranslationAxis 已提交配合保留的平移轴；为空时使用普通平面偏移求解。
 * @returns 单条配合应用结果。
 */
export function solveCoincidentOrDistanceOffset(
  mate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  distance: number,
  epsilon: number,
  alignRepresentativePoints = false,
  constrainedTranslationAxis: THREE.Vector3 | null = null,
): StandardMateApplyResult {
  if (resolvePlaneGeometry(side.fixedEntity) && resolvePlaneGeometry(side.movingEntity)) {
    /**
     * SOLIDWORKS 中平面-平面 Distance 仍然受“同向/反向对齐”控制：
     * 先把移动面法向旋到目标对齐状态，再沿固定面法向应用有符号距离。
     * 其它距离组合的对齐入口由上层禁用，避免圆柱/平面距离模式被误旋转。
     */
    const shouldAlignPlaneDistance = mate.mateType === AssemblyMateType.Distance;
    return solvePlanePlaneOffset(
      mate,
      side,
      distance,
      epsilon,
      mate.mateType === AssemblyMateType.Coincident || shouldAlignPlaneDistance,
      mate.mateType === AssemblyMateType.Coincident && alignRepresentativePoints,
      constrainedTranslationAxis,
    );
  }
  if (mate.mateType === AssemblyMateType.Coincident) {
    const planeCircularEdgeResult = solvePlaneCircularEdgeCoincident(mate, side, epsilon);
    if (planeCircularEdgeResult) {
      return planeCircularEdgeResult;
    }
    const surfaceCoincidentResult = solveSurfaceCoincidentMate(mate, side, epsilon);
    if (surfaceCoincidentResult) {
      return surfaceCoincidentResult;
    }
  }
  if (
    (resolvePlaneGeometry(side.fixedEntity) && resolveAxisGeometry(side.movingEntity))
    || (resolveAxisGeometry(side.fixedEntity) && resolvePlaneGeometry(side.movingEntity))
  ) {
    return solvePlaneAxisOffset(
      side,
      distance,
      epsilon,
      mate.mateType === AssemblyMateType.Coincident,
      mate.mateType === AssemblyMateType.Distance,
    );
  }
  if (resolveDistancePointGeometry(side.movingEntity) || resolveDistancePointGeometry(side.fixedEntity)) {
    /**
     * 点/球心距离的位移方向必须由 solvePointOffset 结合真实 moving/fixed 组件判断。
     * 这里不再交换实体，避免“固定点 + 移动平面/轴线”时把移动组件推到错误一侧。
     */
    return solvePointOffset(side, Math.abs(distance), epsilon);
  }
  if (
    mate.mateType === AssemblyMateType.Coincident
    && resolveAxisGeometry(side.fixedEntity)
    && resolveAxisGeometry(side.movingEntity)
  ) {
    return solveConcentricMate(mate, side, epsilon);
  }
  if (
    mate.mateType === AssemblyMateType.Distance
    && resolveAxisGeometry(side.fixedEntity)
    && resolveAxisGeometry(side.movingEntity)
  ) {
    return solveAxisAxisOffset(mate, side, Math.abs(distance), epsilon, false);
  }

  return {
    status: MateSolveStatus.UnsupportedGeometry,
    message: '当前标准配合的几何组合暂不支持求解。',
  };
}

/**
 * 为距离配合解析当前可尝试的距离偏移候选。
 *
 * @param mate 当前求解步配合定义。
 * @param sourceMate 创建 Mate 时的原始配合定义。
 * @param side 当前移动侧。
 * @param linearDimensionScale 线性尺寸换算比例。
 * @param branchPolicy 距离符号分支策略。
 * @param epsilon 数值容差。
 * @returns 可尝试的求解器内部距离偏移。
 */
export function resolveDistanceOffsetsForBranchPolicy(
  mate: AssemblyMateDefinition,
  sourceMate: AssemblyMateDefinition,
  side: StandardMateMovingSideResolution,
  linearDimensionScale: number,
  branchPolicy: StandardMateDistanceBranchPolicy,
  epsilon: number,
): number[] {
  const planeAxisSignedDistance = resolvePlaneAxisSignedDistanceFromSelectionBranch(
    mate,
    sourceMate,
    linearDimensionScale,
    epsilon,
  );
  const signedDistance = planeAxisSignedDistance
    ?? resolveSignedDistanceForMovingSide(mate, side, linearDimensionScale);
  if (mate.mateType !== AssemblyMateType.Distance) {
    return [signedDistance];
  }

  /**
   * 平面-平面 Distance 的 Flip Dimension 与 Mate Alignment 共同定义了有向距离。
   * 拖动后的连续分支只能用于圆柱/球等对侧不由尺寸方向表达的距离组合；
   * 否则会为了“最近位姿”切到另一侧，破坏 SOLIDWORKS 的反转尺寸语义。
   */
  const usesDirectedPlaneDistance = Boolean(
    resolvePlaneGeometry(side.fixedEntity) && resolvePlaneGeometry(side.movingEntity),
  );
  const signedDistanceCandidates = (
    branchPolicy === StandardMateDistanceBranchPolicy.Continuous
    && !usesDirectedPlaneDistance
    && planeAxisSignedDistance === null
  )
    ? [signedDistance, -signedDistance]
    : [signedDistance];
  const distances: number[] = [];

  signedDistanceCandidates.forEach((candidateSignedDistance) => {
    const distance = resolveStandardMateDistanceOffsetForSolver({
      mate,
      firstEntity: side.fixedEntity,
      secondEntity: side.movingEntity,
      signedDistance: candidateSignedDistance,
    });
    if (distance === null) {
      return;
    }
    if (distances.some((item) => Math.abs(item - distance) <= DISTANCE_SOLVER_EPSILON)) {
      return;
    }
    distances.push(distance);
  });

  return distances;
}

/**
 * 计算候选 transform 相对当前移动件的变化量。
 *
 * @param current 当前 transform。
 * @param candidate 候选 transform。
 * @returns 位移与旋转合成后的评分，越小越接近当前姿态。
 */
function scoreTransformContinuity(
  current: ComponentTransformSnapshot,
  candidate: ComponentTransformSnapshot,
): number {
  const dx = candidate.position[0] - current.position[0];
  const dy = candidate.position[1] - current.position[1];
  const dz = candidate.position[2] - current.position[2];
  const currentQuaternion = current.quaternion;
  const candidateQuaternion = candidate.quaternion;
  const quaternionDot = Math.abs(
    currentQuaternion[0] * candidateQuaternion[0]
    + currentQuaternion[1] * candidateQuaternion[1]
    + currentQuaternion[2] * candidateQuaternion[2]
    + currentQuaternion[3] * candidateQuaternion[3],
  );

  return dx * dx + dy * dy + dz * dz + (1 - Math.min(1, quaternionDot));
}

/**
 * 在多个距离配合候选中选择最连续的求解结果。
 *
 * @param currentTransform 当前移动件 transform。
 * @param results 候选求解结果。
 * @returns 最适合应用的结果。
 */
export function selectContinuousDistanceResult(
  currentTransform: ComponentTransformSnapshot,
  results: readonly StandardMateApplyResult[],
): StandardMateApplyResult {
  const solvedResults = results.filter((result) => (
    result.status === MateSolveStatus.Solved && Boolean(result.transform)
  ));
  if (solvedResults.length === 0) {
    return results[0] ?? {
      status: MateSolveStatus.UnsupportedGeometry,
      message: '当前距离配合条件暂不支持求解。',
    };
  }

  return solvedResults.reduce((best, current) => {
    if (!best.transform) {
      return current;
    }
    if (!current.transform) {
      return best;
    }
    return scoreTransformContinuity(currentTransform, current.transform)
      < scoreTransformContinuity(currentTransform, best.transform)
      ? current
      : best;
  });
}
